import { useMemo } from 'react'
import { Card } from '@/components/ui'
import { getPartnerMarketingTargetKey } from '@/lib/partnerMarketingWeeklyPlan'
import { SURFACE_CARD_CLASS } from './marketingClasses'


export type PartnerMarketingAnalysisRow = {
  date: string
  service: string
  accountId: number
  accountCurrency: string | null
  cost: number
  conversions: number
}

export type MarketingFactorAlert = {
  id: string
  severity: 'critical' | 'warning' | 'positive' | 'info'
  title: string
  message: string
  accountLabel?: string
  accountDisplayLabel?: string
  periodLabel?: string
}

export type MarketingPlanTotals = {
  spend: number
  leads: number
  spendByCurrency: Record<string, number>
  cpa: number | null
}

type DailyMarketingAggregate = {
  spend: number
  spendByCurrency: Record<string, number>
  conversions: number
  cpa: number | null
}

type MarketingFactorAlertParams = {
  rows: PartnerMarketingAnalysisRow[]
  planTotals: MarketingPlanTotals
  includeVat: boolean
  hasMixedCurrencies: boolean
  selectedCurrency: string | null
  getAccountCurrency: (value: string | null | undefined) => string
  getAccountDisplayLabel: (row: PartnerMarketingAnalysisRow) => string
  formatMoneyByCurrencies: (spendByCurrency: Record<string, number>, includeVat?: boolean) => string
  formatMoneyValue: (value: number, currency: string | null, includeVat?: boolean) => string
  getSingleCurrency: (spendByCurrency: Record<string, number>) => string | null
}

function parseDateKey(dateKey: string): Date {
  const [year, month, day] = dateKey.split('-').map(Number)
  return new Date(Date.UTC(year, (month || 1) - 1, day || 1))
}

function formatDateKey(date: Date): string {
  const year = date.getUTCFullYear()
  const month = String(date.getUTCMonth() + 1).padStart(2, '0')
  const day = String(date.getUTCDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function shiftDateKey(dateKey: string, deltaDays: number): string {
  const date = parseDateKey(dateKey)
  date.setUTCDate(date.getUTCDate() + deltaDays)
  return formatDateKey(date)
}

export function formatAnalysisDate(dateKey: string): string {
  const [year, month, day] = dateKey.split('-')
  return `${day}.${month}.${year}`
}

function formatAnalysisDateRange(dateKeys: string[]): string {
  if (dateKeys.length === 0) return ''
  if (dateKeys.length === 1) return formatAnalysisDate(dateKeys[0])
  return `${formatAnalysisDate(dateKeys[0])} - ${formatAnalysisDate(dateKeys[dateKeys.length - 1])}`
}

export function getLatestDateKey<Row extends { date: string }>(rows: Row[]): string | null {
  if (rows.length === 0) return null
  return rows.reduce((latest, row) => (row.date > latest ? row.date : latest), rows[0].date)
}

function getEarliestDateKey(rows: PartnerMarketingAnalysisRow[]): string | null {
  if (rows.length === 0) return null
  return rows.reduce((earliest, row) => (row.date < earliest ? row.date : earliest), rows[0].date)
}

export function getDateRangeKeysEndingAt(endDateKey: string, days: number): string[] {
  if (days <= 0) return []
  const range: string[] = []

  for (let offset = days - 1; offset >= 0; offset -= 1) {
    range.push(shiftDateKey(endDateKey, -offset))
  }

  return range
}

export function filterRowsByLastNDays<Row extends { date: string }>(rows: Row[], days: number): Row[] {
  const latestDateKey = getLatestDateKey(rows)
  if (!latestDateKey) return []

  const allowedDates = new Set(getDateRangeKeysEndingAt(latestDateKey, days))
  return rows.filter((row) => allowedDates.has(row.date))
}

function hasDateCoverage(rows: PartnerMarketingAnalysisRow[], endDateKey: string, days: number): boolean {
  const earliestDateKey = getEarliestDateKey(rows)
  if (!earliestDateKey) return false

  const requiredStartDateKey = shiftDateKey(endDateKey, -(days - 1))
  return earliestDateKey <= requiredStartDateKey
}

function hasDailyRowsCoverage(
  dailyRowsByDate: Map<string, PartnerMarketingAnalysisRow[]>,
  endDateKey: string,
  days: number
): boolean {
  const dateKeys = Array.from(dailyRowsByDate.keys())
  if (dateKeys.length === 0) return false

  const earliestDateKey = dateKeys.reduce((earliest, current) => (current < earliest ? current : earliest), dateKeys[0])
  const requiredStartDateKey = shiftDateKey(endDateKey, -(days - 1))
  return earliestDateKey <= requiredStartDateKey
}

function computeDailyAggregate(
  rows: PartnerMarketingAnalysisRow[],
  getAccountCurrency: (value: string | null | undefined) => string
): DailyMarketingAggregate {
  const spendByCurrency: Record<string, number> = {}
  let spend = 0
  let conversions = 0

  rows.forEach((row) => {
    spend += row.cost
    conversions += row.conversions
    const currency = getAccountCurrency(row.accountCurrency)
    spendByCurrency[currency] = (spendByCurrency[currency] || 0) + row.cost
  })

  return {
    spend,
    spendByCurrency,
    conversions,
    cpa: conversions > 0 ? spend / conversions : null
  }
}

function getAggregateForDateKeys(
  dailyRowsByDate: Map<string, PartnerMarketingAnalysisRow[]>,
  dateKeys: string[],
  getAccountCurrency: (value: string | null | undefined) => string
): DailyMarketingAggregate {
  return computeDailyAggregate(dateKeys.flatMap((dateKey) => dailyRowsByDate.get(dateKey) || []), getAccountCurrency)
}

function getAverageSpendForDateKeys(
  dailyRowsByDate: Map<string, PartnerMarketingAnalysisRow[]>,
  dateKeys: string[],
  getAccountCurrency: (value: string | null | undefined) => string
): number | null {
  if (dateKeys.length === 0) return null
  const aggregate = getAggregateForDateKeys(dailyRowsByDate, dateKeys, getAccountCurrency)
  return aggregate.spend / dateKeys.length
}

function getMedian(values: number[]): number | null {
  if (values.length === 0) return null
  const sorted = [...values].sort((a, b) => a - b)
  const middleIndex = Math.floor(sorted.length / 2)

  if (sorted.length % 2 === 0) {
    return (sorted[middleIndex - 1] + sorted[middleIndex]) / 2
  }

  return sorted[middleIndex]
}

function getMarketingAlertAccountDisplayLabel(accountLabel: string, serviceLabel: string | null | undefined): string {
  const normalizedService = serviceLabel?.trim()
  if (!normalizedService) return accountLabel
  return `${accountLabel} • ${normalizedService}`
}

export function buildMarketingFactorAlerts({
  rows,
  planTotals,
  includeVat,
  hasMixedCurrencies,
  selectedCurrency,
  getAccountCurrency,
  getAccountDisplayLabel,
  formatMoneyByCurrencies,
  formatMoneyValue,
  getSingleCurrency
}: MarketingFactorAlertParams): MarketingFactorAlert[] {
  const analysisDate = getLatestDateKey(rows)
  if (!analysisDate) return []

  const targetRowsByKey = new Map<string, { accountLabel: string; serviceLabel: string; dailyRowsByDate: Map<string, PartnerMarketingAnalysisRow[]> }>()
  const totalRowsByDate = new Map<string, PartnerMarketingAnalysisRow[]>()

  rows.forEach((row) => {
    const targetKey = getPartnerMarketingTargetKey(row.service, row.accountId)
    const target = targetRowsByKey.get(targetKey) || {
      accountLabel: getAccountDisplayLabel(row),
      serviceLabel: row.service,
      dailyRowsByDate: new Map<string, PartnerMarketingAnalysisRow[]>()
    }
    const targetDayRows = target.dailyRowsByDate.get(row.date) || []
    targetDayRows.push(row)
    target.dailyRowsByDate.set(row.date, targetDayRows)
    targetRowsByKey.set(targetKey, target)

    const totalDayRows = totalRowsByDate.get(row.date) || []
    totalDayRows.push(row)
    totalRowsByDate.set(row.date, totalDayRows)
  })

  const alerts: MarketingFactorAlert[] = []
  const day1Keys = getDateRangeKeysEndingAt(analysisDate, 1)
  const day2Keys = getDateRangeKeysEndingAt(analysisDate, 2)
  const day3Keys = getDateRangeKeysEndingAt(analysisDate, 3)
  const day7Keys = getDateRangeKeysEndingAt(analysisDate, 7)
  const previousFiveBeforeLastTwoKeys = getDateRangeKeysEndingAt(shiftDateKey(analysisDate, -2), 5)
  const analysisDateLabel = formatAnalysisDateRange(day1Keys)
  const lastTwoDaysLabel = formatAnalysisDateRange(day2Keys)
  const lastThreeDaysLabel = formatAnalysisDateRange(day3Keys)
  const lastSevenDaysLabel = formatAnalysisDateRange(day7Keys)
  const previousFiveDaysLabel = formatAnalysisDateRange(previousFiveBeforeLastTwoKeys)
  const totalDayAggregate = getAggregateForDateKeys(totalRowsByDate, day1Keys, getAccountCurrency)
  const dailyPlanSpend = planTotals.spend > 0 ? planTotals.spend / 7 : null

  targetRowsByKey.forEach(({ accountLabel, serviceLabel, dailyRowsByDate }, targetKey) => {
    const accountDayAggregate = getAggregateForDateKeys(dailyRowsByDate, day1Keys, getAccountCurrency)
    const accountDisplayLabel = getMarketingAlertAccountDisplayLabel(accountLabel, serviceLabel)

    if (accountDayAggregate.spend > 2000 && accountDayAggregate.conversions === 0) {
      alerts.push({
        id: `rule-1:${targetKey}`,
        severity: 'critical',
        title: 'Не было лидов',
        message: `Потрачено ${formatMoneyByCurrencies(accountDayAggregate.spendByCurrency, includeVat)}, лидов нет. Запроси, что делает подрядчик: возможно бюджет сливается без результата.`,
        accountLabel,
        accountDisplayLabel,
        periodLabel: analysisDateLabel
      })
    }

    if (hasDateCoverage(rows, analysisDate, 7) && hasDailyRowsCoverage(dailyRowsByDate, analysisDate, 7)) {
      const cpaLastTwoDays = getAggregateForDateKeys(dailyRowsByDate, day2Keys, getAccountCurrency).cpa
      const cpaLastSevenDays = getAggregateForDateKeys(dailyRowsByDate, day7Keys, getAccountCurrency).cpa

      if (cpaLastTwoDays !== null && cpaLastSevenDays !== null && cpaLastTwoDays > cpaLastSevenDays) {
        alerts.push({
          id: `rule-2:${targetKey}`,
          severity: 'warning',
          title: 'Цена лида выросла',
          message: `Цена лида за этот интервал выше своей средней за ${lastSevenDaysLabel} на ${formatMoneyValue(cpaLastTwoDays - cpaLastSevenDays, getSingleCurrency(accountDayAggregate.spendByCurrency) || selectedCurrency, includeVat)}. Запроси, что происходит с рекламой: возможно подрядчик не следит за открутом и сливает бюджет.`,
          accountLabel,
          accountDisplayLabel,
          periodLabel: lastTwoDaysLabel
        })
      }

      const averageSpendLastSevenDays = getAverageSpendForDateKeys(dailyRowsByDate, day7Keys, getAccountCurrency)
      if (averageSpendLastSevenDays !== null && averageSpendLastSevenDays > 0 && accountDayAggregate.spend <= averageSpendLastSevenDays * 0.5) {
        alerts.push({
          id: `rule-5:${targetKey}`,
          severity: 'warning',
          title: 'Расход упал более чем на 50%',
          message: `Расход упал более чем на 50% относительно среднего дневного расхода за ${lastSevenDaysLabel}. Возможно, кампании остановлены или в кабинете недостаточно бюджета. Уточни причину.`,
          accountLabel,
          accountDisplayLabel,
          periodLabel: analysisDateLabel
        })
      }
    }

    if (hasDateCoverage(rows, analysisDate, 3) && hasDailyRowsCoverage(dailyRowsByDate, analysisDate, 3)) {
      const cpaByDay = day3Keys.map((dateKey) => getAggregateForDateKeys(dailyRowsByDate, [dateKey], getAccountCurrency).cpa)
      if (cpaByDay.every((value) => value !== null) && Number(cpaByDay[0]) < Number(cpaByDay[1]) && Number(cpaByDay[1]) < Number(cpaByDay[2])) {
        alerts.push({
          id: `rule-11:${targetKey}`,
          severity: 'warning',
          title: 'Стоимость заявки растет 3 дня подряд',
          message: 'Возможна усталость связок: реклама выгорает, а новые макеты не запускаются. Уточни, когда в последний раз запускались новые рекламные макеты и какие именно.',
          accountLabel,
          accountDisplayLabel,
          periodLabel: lastThreeDaysLabel
        })
      }
    }
  })

  if (!hasMixedCurrencies && hasDateCoverage(rows, analysisDate, 7)) {
    const cpaRows = Array.from(targetRowsByKey.entries())
      .map(([targetKey, value]) => ({
        targetKey,
        accountLabel: value.accountLabel,
        accountDisplayLabel: getMarketingAlertAccountDisplayLabel(value.accountLabel, value.serviceLabel),
        aggregate: getAggregateForDateKeys(value.dailyRowsByDate, day7Keys, getAccountCurrency)
      }))
      .filter((item) => hasDailyRowsCoverage(targetRowsByKey.get(item.targetKey)!.dailyRowsByDate, analysisDate, 7))
      .filter((item) => item.aggregate.cpa !== null)

    cpaRows.forEach((item) => {
      const otherRows = cpaRows.filter((candidate) => candidate.targetKey !== item.targetKey)
      if (otherRows.length === 0) return

      const othersAggregate = computeDailyAggregate(
        otherRows.flatMap((candidate) =>
          day7Keys.flatMap((dateKey) => targetRowsByKey.get(candidate.targetKey)?.dailyRowsByDate.get(dateKey) || [])
        ),
        getAccountCurrency
      )

      if (item.aggregate.cpa !== null && othersAggregate.cpa !== null && item.aggregate.cpa >= othersAggregate.cpa * 1.25) {
        const diffPercent = Math.round(((item.aggregate.cpa / othersAggregate.cpa) - 1) * 100)
        alerts.push({
          id: `rule-3:${item.targetKey}`,
          severity: 'warning',
          title: 'Цена лида выше остальных на заметную величину',
          message: `Цена лида на ${diffPercent}% выше, чем у остальных подрядчиков за тот же период. Проверь, окупается ли эта реклама наравне с остальными. Если качество и дальнейшие конверсии не лучше, обсуди снижение стоимости лида или замену подрядчика.`,
          accountLabel: item.accountLabel,
          accountDisplayLabel: item.accountDisplayLabel,
          periodLabel: lastSevenDaysLabel
        })
      }
    })

    const medianCpa = getMedian(cpaRows.map((item) => item.aggregate.cpa as number))
    if (medianCpa !== null) {
      cpaRows.forEach((item) => {
        if (item.aggregate.cpa !== null && item.aggregate.cpa <= medianCpa * 0.85) {
          alerts.push({
            id: `rule-4:${item.targetKey}`,
            severity: 'positive',
            title: 'Цена лида заметно лучше средней',
            message: 'Цена лида как минимум на 15% ниже медианы по подрядчикам. Если качество заявок хорошее, можно увеличить бюджет примерно на 20%. Заодно запроси макеты рекламы и проверь, что они соответствуют твоим требованиям.',
            accountLabel: item.accountLabel,
            accountDisplayLabel: item.accountDisplayLabel,
            periodLabel: lastSevenDaysLabel
          })
        }
      })
    }
  }

  if (totalDayAggregate.spend === 0) {
    alerts.push({
      id: 'rule-6',
      severity: 'critical',
      title: 'Расход по рекламе остановлен',
      message: 'Интернет-реклама остановлена. Пополни бюджет, чтобы приходили новые заявки и в студии были пробницы.',
      periodLabel: analysisDateLabel
    })
  }

  if (dailyPlanSpend !== null && dailyPlanSpend > 0) {
    if (totalDayAggregate.spend < dailyPlanSpend * 0.5) {
      alerts.push({
        id: 'rule-8',
        severity: 'critical',
        title: 'Расход ниже дневного плана более чем в 2 раза',
        message: 'Бюджет на рекламу ниже планируемого более чем в два раза. Дневной план считается от недельного плана расхода. Реклама фактически недокручивается, есть риск недобора лидов по неделе.',
        periodLabel: analysisDateLabel
      })
    } else if (totalDayAggregate.spend < dailyPlanSpend * 0.8) {
      alerts.push({
        id: 'rule-7',
        severity: 'warning',
        title: 'Общий расход ниже дневного плана',
        message: 'Общий расход ниже дневного плана. Дневной план считается от недельного плана расхода. Недоиспользуется рекламный бюджет, возможна потеря заявок. Проверь, знают ли подрядчики, какой бюджет они должны реализовывать.',
        periodLabel: analysisDateLabel
      })
    }

    if (totalDayAggregate.spend > dailyPlanSpend * 1.5) {
      alerts.push({
        id: 'rule-9',
        severity: 'warning',
        title: 'Расход выше дневного плана',
        message: 'Превышен дневной темп расхода. Дневной план считается от недельного плана расхода. Бюджет тратится быстрее плана, возможен перерасход к концу недели.',
        periodLabel: analysisDateLabel
      })
    }
  }

  if (hasDateCoverage(rows, analysisDate, 7)) {
    const previousDayTotal = getAggregateForDateKeys(totalRowsByDate, [shiftDateKey(analysisDate, -1)], getAccountCurrency)
    const previousFiveAverageSpend = getAverageSpendForDateKeys(totalRowsByDate, previousFiveBeforeLastTwoKeys, getAccountCurrency)

    if (
      previousFiveAverageSpend !== null &&
      previousFiveAverageSpend > 0 &&
      totalDayAggregate.spend < previousFiveAverageSpend * 0.7 &&
      previousDayTotal.spend < previousFiveAverageSpend * 0.7
    ) {
      alerts.push({
        id: 'rule-10',
        severity: 'warning',
        title: 'Расход снижается 2 дня подряд',
        message: `Суммарный расход значительно ниже среднего дневного расхода за предыдущие 5 дней (${previousFiveDaysLabel}). Возможно, остановлены кампании, ограничен открут или недостаточно бюджета в рекламных кабинетах.`,
        periodLabel: lastTwoDaysLabel
      })
    }
  }

  return alerts
}

export function MarketingFactorAnalysisCard({
  alerts,
  analysisDate
}: {
  alerts: MarketingFactorAlert[]
  analysisDate: string | null
}) {
  const sortedAlerts = useMemo(() => {
    const severityOrder: Record<MarketingFactorAlert['severity'], number> = {
      critical: 0,
      warning: 1,
      positive: 2,
      info: 3
    }

    return [...alerts].sort((a, b) => {
      const severityDiff = severityOrder[a.severity] - severityOrder[b.severity]
      if (severityDiff !== 0) return severityDiff
      return a.title.localeCompare(b.title, 'ru')
    })
  }, [alerts])

  const severityClasses: Record<MarketingFactorAlert['severity'], string> = {
    critical: 'border-red-200 bg-red-50 text-red-900',
    warning: 'border-amber-200 bg-amber-50 text-amber-900',
    positive: 'border-green-200 bg-green-50 text-green-900',
    info: 'border-border bg-muted text-muted-foreground'
  }

  const severityLabels: Record<MarketingFactorAlert['severity'], string> = {
    critical: 'Критично',
    warning: 'Внимание',
    positive: 'Возможность',
    info: 'Инсайт'
  }

  return (
    <Card className={`${SURFACE_CARD_CLASS} self-start`}>
      <div className="space-y-4">
        <div className="space-y-1">
          <h2 className="text-base font-semibold">Анализ VK ADS</h2>
          <p className="text-xs text-muted-foreground">Показываем отклонения только по VK ADS. Direct в этот блок не входит.</p>
          {analysisDate ? <p className="text-xs text-muted-foreground">Последняя дата в данных: {formatAnalysisDate(analysisDate)}</p> : null}
        </div>

        {!analysisDate ? (
          <div className="text-sm text-muted-foreground">Недостаточно данных по VK ADS для анализа.</div>
        ) : sortedAlerts.length === 0 ? (
          <div className="text-sm text-muted-foreground">Критичных отклонений по последней выгруженной дате и последним периодам не найдено.</div>
        ) : (
          <div className="space-y-2.5">
            {sortedAlerts.map((alert) => (
              <div key={alert.id} className={`rounded-2xl border p-3 ${severityClasses[alert.severity]}`}>
                {alert.accountDisplayLabel || alert.accountLabel ? (
                  <div className="mb-2 text-base font-semibold leading-tight">
                    {alert.accountDisplayLabel || alert.accountLabel}
                  </div>
                ) : null}
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <span className="rounded-full border border-current/15 bg-card/60 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em]">
                    {severityLabels[alert.severity]}
                  </span>
                  {alert.periodLabel ? (
                    <span className="rounded-full border border-current/15 bg-card/40 px-2 py-0.5 text-[11px] opacity-80">
                      {alert.periodLabel}
                    </span>
                  ) : null}
                </div>
                <div className="text-[15px] font-semibold leading-snug">{alert.title}</div>
                <div className="mt-1.5 text-sm leading-6 opacity-90">{alert.message}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  )
}
